/**
 * §11.8 flash standards and photosafety (spine §14).
 *
 * Full-screen flash = any flash covering > 25% of the framebuffer. Limits: luminance
 * ≤ 0.35, duration ≤ 120 ms, rate ≤ 3/s — the rate is a spine-level hard cap, so this
 * controller *refuses* a fourth request inside a rolling second rather than shortening
 * it. Reduced-flashes mode tightens all three to 0.15 / 60 ms / 1 per second.
 *
 * There is deliberately no way to ask for a flash outside these bounds.
 */

const MAX_LUMINANCE = 0.35;
const MAX_DURATION_S = 0.12;
const MAX_RATE_PER_S = 3;

const REDUCED_LUMINANCE = 0.15;
const REDUCED_DURATION_S = 0.06;
const REDUCED_RATE_PER_S = 1;

const WINDOW_S = 1.0;

export class FlashController {
  /** §13 reduced-flashes accessibility toggle. */
  reduced = false;

  private r = 1;
  private g = 1;
  private b = 1;
  private remaining = 0;
  private duration = 0;
  private peak = 0;
  /** Ring of recent trigger timestamps for the rolling-window rate check. */
  private readonly stamps = new Float64Array(MAX_RATE_PER_S);
  private stampCursor = 0;
  private time = 0;

  /** Requests a full-screen flash. Returns false when the rate cap refused it. */
  request(r: number, g: number, b: number, luminance: number, durationS: number): boolean {
    const rate = this.reduced ? REDUCED_RATE_PER_S : MAX_RATE_PER_S;
    let used = 0;
    for (let i = 0; i < rate; i++) {
      if (this.time - this.stamps[i] < WINDOW_S) used++;
    }
    if (used >= rate) return false;

    this.stamps[this.stampCursor] = this.time;
    this.stampCursor = (this.stampCursor + 1) % this.stamps.length;

    this.r = r;
    this.g = g;
    this.b = b;
    this.peak = Math.min(luminance, this.reduced ? REDUCED_LUMINANCE : MAX_LUMINANCE);
    this.duration = Math.min(durationS, this.reduced ? REDUCED_DURATION_S : MAX_DURATION_S);
    this.remaining = this.duration;
    return true;
  }

  update(dt: number): void {
    this.time += dt;
    if (this.remaining > 0) {
      this.remaining = Math.max(0, this.remaining - dt);
    }
  }

  /** Current strength, 0 when nothing is firing. Linear ramp down over the duration. */
  get strength(): number {
    if (this.remaining <= 0 || this.duration <= 0) return 0;
    return this.peak * (this.remaining / this.duration);
  }

  get colorR(): number {
    return this.r;
  }

  get colorG(): number {
    return this.g;
  }

  get colorB(): number {
    return this.b;
  }

  reset(): void {
    this.remaining = 0;
    this.stamps.fill(-WINDOW_S * 4);
    this.stampCursor = 0;
  }
}
